import { defineStore } from 'pinia'
import { ref } from 'vue'
import { CacheManager } from '../utils/cacheManager'
import { httpsCallable } from 'firebase/functions'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { useAuthStore } from './Auth'
import { firestore, functions } from '../firebase'
import { EntityType, ReportableEntity, type ReportDataRead, ReportReason } from '../utils/types'
import type {
  UserDetailResponse,
  CreatePostRequest,
  UpdatePostRequest,
  DeletePostRequest,
  PostDataRead,
  PostDetailResponse,
  AddCommentRequest,
  UpdateCommentRequest,
  DeleteCommentRequest,
  CommentDataRead,
  CommentDetailResponse,
  AddReplyRequest,
  UpdateReplyRequest,
  DeleteReplyRequest,
  ReplyDataRead,
  ReplyDetailResponse,
  TogglePostLikeRequest,
  ToggleCommentLikeRequest,
  ToggleReplyLikeRequest,
  ToggleLikeResponse,
  ReportDetailResponse,
  UserReportRequest,
  PostReportRequest,
  CommentReportRequest,
  ReplyReportRequest,
  UserAppealRequest,
  PostAppealRequest,
  CommentAppealRequest,
  ReplyAppealRequest,
  AppealDetailResponse,
  PostPreviewResponse,
  CommentPreviewResponse,
  ReplyPreviewResponse,
} from '../utils/types'

// Cache managers with TTL and max entries
const postCache = new CacheManager<PostPreviewResponse>(5 * 60 * 1000, 100, 24 * 60 * 60 * 1000)
const commentCache = new CacheManager<CommentPreviewResponse>(
  3 * 60 * 1000,
  200,
  24 * 60 * 60 * 1000,
)
const replyCache = new CacheManager<ReplyPreviewResponse>(
  3 * 60 * 60 * 1000,
  300,
  24 * 60 * 60 * 1000,
)

// Firebase Functions initialization
const createPostFunc = httpsCallable(functions, 'createPost')
const updatePostFunc = httpsCallable(functions, 'updatePost')
const deletePostFunc = httpsCallable(functions, 'deletePost')
const addCommentFunc = httpsCallable(functions, 'addComment')
const updateCommentFunc = httpsCallable(functions, 'updateComment')
const deleteCommentFunc = httpsCallable(functions, 'deleteComment')
const addReplyFunc = httpsCallable(functions, 'addReply')
const updateReplyFunc = httpsCallable(functions, 'updateReply')
const deleteReplyFunc = httpsCallable(functions, 'deleteReply')
const togglePostLikeFunc = httpsCallable(functions, 'togglePostLike')
const toggleCommentLikeFunc = httpsCallable(functions, 'toggleCommentLike')
const toggleReplyLikeFunc = httpsCallable(functions, 'toggleReplyLike')
const reportUserFunc = httpsCallable(functions, 'reportUser')
const reportPostFunc = httpsCallable(functions, 'reportPost')
const reportCommentFunc = httpsCallable(functions, 'reportComment')
const reportReplyFunc = httpsCallable(functions, 'reportReply')
const manageReportFunc = httpsCallable(functions, 'manageReport')
const submitUserAppealFunc = httpsCallable(functions, 'submitUserAppeal')
const submitPostAppealFunc = httpsCallable(functions, 'submitPostAppeal')
const submitCommentAppealFunc = httpsCallable(functions, 'submitCommentAppeal')
const submitReplyAppealFunc = httpsCallable(functions, 'submitReplyAppeal')
const reviewAppealFunc = httpsCallable(functions, 'reviewAppeal')
const getUserProfileFunc = httpsCallable(functions, 'getUserProfile')
const getUserProfilePostsFunc = httpsCallable(functions, 'getUserProfilePosts')
const getPostsByLikesFunc = httpsCallable(functions, 'getPostsByLikes')
const getPostsByUserFunc = httpsCallable(functions, 'getPostsByUser')
const getCommentsByUserFunc = httpsCallable(functions, 'getCommentsByUser')
const getRepliesByUserFunc = httpsCallable(functions, 'getRepliesByUser')
const getPostDetailFunc = httpsCallable(functions, 'getPostDetails')
const getReportsFunc = httpsCallable(functions, 'getReports')
const getUserAppealsFunc = httpsCallable(functions, 'getUserAppeals')
const getPendingAppealsFunc = httpsCallable(functions, 'getPendingAppeals')

export const useCommunityStore = defineStore('community', {
  state: () => ({
    postsMap: {} as Record<string, PostPreviewResponse>,
    profiles: {} as Record<
      string,
      { data: UserDetailResponse | null; loading: boolean; error: string | null }
    >,
    profilePosts: {} as Record<string, { postIds: string[]; nextStartAfter: string | null }>,
    myPosts: { postIds: [] as string[], nextStartAfter: null as string | null },
    postsList: [] as PostPreviewResponse[],
    hasMorePosts: true,
    nextPostPageToken: null as string | [number, string] | null,
    currentPost: ref<PostDetailResponse | null>(null),
    appealsList: [] as AppealDetailResponse[],
    pendingReports: [] as ReportDetailResponse[],
    pendingAppeals: [] as AppealDetailResponse[],
    myComments: { commentIds: [] as string[], nextStartAfter: null as string | null },
    myReplies: { replyIds: [] as string[], nextStartAfter: null as string | null },
    myAppeals: { appealIds: [] as string[], nextStartAfter: null as string | null },
    loading: {
      posts: false,
      postDetails: false,
      comments: false,
      replies: false,
      likes: false,
      reports: false,
      appeals: false,
    },
    isAdmin: null as boolean | null,
    commentStartAfter: null as string | null,
    nextCommentPageToken: null as string | null,
    replyStartAfter: null as string | null,
    nextReplyPageToken: {} as Record<string, string | null>,
    nextStartAfter: null as string | null,
  }),

  getters: {
    getUserProfile: (state) => (userId: string) => state.profiles[userId]?.data || null,
    getProfilePosts: (state) => (userId: string) => {
      const profile = state.profilePosts[userId]
      if (!profile) return []
      return profile.postIds
        .map((id) => state.postsMap[id])
        .filter(Boolean) as PostPreviewResponse[]
    },
    visiblePosts: (state) => state.postsList.filter((post) => post.isVisible),
    visibleComments: (state) =>
      state.currentPost?.comments?.filter((comment) => comment.isVisible) || [],
    visibleReplies: (state) => (commentId: string) =>
      state.currentPost?.comments
        ?.find((comment) => comment.id === commentId)
        ?.replies?.filter((reply) => reply.isVisible) || [],
    getMyPosts: (state) => {
      return state.myPosts.postIds
        .map((id) => state.postsMap[id])
        .filter((post): post is PostPreviewResponse => !!post)
    },
    getMyComments: (state) => {
      return state.myComments.commentIds
        .map((id) => commentCache.get(id))
        .filter((comment): comment is CommentPreviewResponse => !!comment)
    },
    getMyReplies: (state) => {
      return state.myReplies.replyIds
        .map((id) => replyCache.get(id))
        .filter((reply): reply is ReplyPreviewResponse => !!reply)
    },
    getMyAppeals: (state) => {
      return state.myAppeals.appealIds
        .map((id) => state.appealsList.find((appeal) => appeal.id === id))
        .filter((appeal): appeal is AppealDetailResponse => !!appeal)
    },
  },

  actions: {
    /**
     * Generates a Firestore path for an entity based on its type and hierarchy.
     */
    generatePath(
      entityType: EntityType,
      id: string,
      parentId?: string,
      grandparentId?: string,
      greatGrandparentId?: string,
    ): string {
      switch (entityType) {
        case EntityType.Post:
          if (!parentId) throw new Error('parentId required for Post path')
          return `users/${parentId}/posts/${id}`
        case EntityType.Comment:
          if (!parentId || !grandparentId)
            throw new Error('parentId and grandparentId required for Comment path')
          return `users/${grandparentId}/posts/${parentId}/comments/${id}`
        case EntityType.Reply:
          if (!parentId || !grandparentId || !greatGrandparentId)
            throw new Error(
              'parentId, grandparentId, and greatGrandparentId required for Reply path',
            )
          return `users/${greatGrandparentId}/posts/${grandparentId}/comments/${parentId}/replies/${id}`
        default:
          throw new Error('Invalid entity type')
      }
    },

    /**
     * Handles errors with detailed logging and user-friendly messages.
     */
    handleError(error: unknown, context?: string): string {
      console.error(`Error in ${context || 'unknown context'}:`, error)
      if (error instanceof Error && 'code' in error) {
        switch (error.code) {
          case 'UNAUTHENTICATED':
            return '인증이 필요합니다.'
          case 'PERMISSION_DENIED':
            return '권한이 없습니다.'
          case 'NOT_FOUND':
            return '요청한 리소스를 찾을 수 없습니다.'
          case 'VISIBILITY_RESTRICTED':
            return '이 콘텐츠는 현재 표시되지 않습니다.'
          case 'INVALID_INPUT':
            return '입력값이 올바르지 않습니다.'
          case 'RESOURCE_EXHAUSTED':
            return '리소스 제한에 도달했습니다. 나중에 다시 시도해주세요.'
          case 'DATA_LOSS':
            return '데이터 손실이 발생했습니다. 관리자에게 문의해주세요.'
          case 'TRANSACTION_ERROR':
            return '트랜잭션 처리 중 오류가 발생했습니다. 다시 시도해주세요.'
          default:
            return error.message || '예기치 않은 오류가 발생했습니다.'
        }
      }
      return error instanceof Error ? error.message : '예기치 않은 오류가 발생했습니다.'
    },

    /**
     * Ensures the user is authenticated and initializes admin status.
     */
    async ensureAuthenticated(): Promise<string> {
      const authStore = useAuthStore()
      if (!authStore.isInitialized) await authStore.initializeAuth()
      if (!authStore.isAuthenticated || !authStore.user) {
        throw new Error('인증이 필요합니다.')
      }
      if (this.isAdmin === null) {
        await authStore.checkAdminStatus()
        this.isAdmin = authStore.isAdmin ?? false
      }
      return authStore.getUserId()
    },

    /**
     * Validates appeal request data.
     */
    validateAppealRequest(
      request: UserAppealRequest | PostAppealRequest | CommentAppealRequest | ReplyAppealRequest,
      type: 'user' | 'post' | 'comment' | 'reply',
    ): boolean {
      if (!request.reason) {
        throw new Error('Reason is required for submitting an appeal')
      }
      if (type === 'user') {
        const userRequest = request as UserAppealRequest
        if (!userRequest.userId) {
          throw new Error('reportedUserId is required for user appeals')
        }
      } else if (type === 'post') {
        const postRequest = request as PostAppealRequest
        if (!postRequest.postId) {
          throw new Error('postId is required for post appeals')
        }
      } else if (type === 'comment') {
        const commentRequest = request as CommentAppealRequest
        if (!commentRequest.commentId || !commentRequest.postId) {
          throw new Error('commentId and postId are required for comment appeals')
        }
      } else if (type === 'reply') {
        const replyRequest = request as ReplyAppealRequest
        if (!replyRequest.replyId || !replyRequest.postId || !replyRequest.commentId) {
          throw new Error('replyId, postId, and commentId are required for reply appeals')
        }
      }
      return true
    },

    /**
     * Creates a post preview from a detailed response.
     */
    createPostPreview(post: PostDetailResponse): PostPreviewResponse {
      return {
        id: post.id,
        userId: post.userId,
        created_at: post.created_at,
        updated_at: post.updated_at,
        isVisible: post.isVisible,
        title: post.title,
        content: post.content,
        authorType: post.authorType,
        authorName: post.authorName,
        commentCount: post.commentCount,
        likeCount: post.likeCount,
        reportsReceived: post.reportsReceived,
        appealStatus: post.appealStatus,
        isLikedByCurrentUser: post.isLikedByCurrentUser,
        path: this.generatePath(EntityType.Post, post.id, post.userId),
      }
    },

    /**
     * Creates a comment preview from a detailed response.
     */
    createCommentPreview(
      comment: CommentDetailResponse | CommentPreviewResponse,
      postId: string,
    ): CommentPreviewResponse {
      return {
        postOwnerId: comment.postOwnerId,
        id: comment.id,
        postId,
        userId: comment.userId,
        created_at: comment.created_at,
        updated_at: comment.updated_at,
        isVisible: comment.isVisible,
        content: comment.content,
        authorType: comment.authorType,
        authorName: comment.authorName,
        replyCount: comment.replyCount,
        likeCount: comment.likeCount,
        reportsReceived: comment.reportsReceived,
        appealStatus: comment.appealStatus,
        isLikedByCurrentUser: comment.isLikedByCurrentUser,
        path: this.generatePath(EntityType.Comment, comment.id, postId, comment.postOwnerId),
      }
    },

    /**
     * Creates a reply preview from a detailed response.
     */
    createReplyPreview(
      reply: ReplyDetailResponse | ReplyPreviewResponse,
      postId: string,
      commentId: string,
    ): ReplyPreviewResponse {
      return {
        postOwnerId: reply.postOwnerId,
        id: reply.id,
        postId,
        commentId,
        userId: reply.userId,
        created_at: reply.created_at,
        updated_at: reply.updated_at,
        isVisible: reply.isVisible,
        content: reply.content,
        authorType: reply.authorType,
        authorName: reply.authorName,
        likeCount: reply.likeCount,
        reportsReceived: reply.reportsReceived,
        appealStatus: reply.appealStatus,
        isLikedByCurrentUser: reply.isLikedByCurrentUser,
        path: this.generatePath(EntityType.Reply, reply.id, commentId, postId, reply.postOwnerId),
      }
    },

    /**
     * Updates like state for posts, comments, or replies.
     */
    updateLikeState(
      entityType: 'post' | 'comment' | 'reply',
      entityId: string,
      isLiked: boolean,
      likeCount: number,
      updatedAt?: string | null,
      commentId?: string,
    ) {
      if (entityType === 'post') {
        const post = this.postsMap[entityId]
        if (post) {
          post.isLikedByCurrentUser = isLiked
          post.likeCount = likeCount
          if (updatedAt !== undefined) {
            post.updated_at = updatedAt
          }
          const listIndex = this.postsList.findIndex((p) => p.id === entityId)
          if (listIndex !== -1) {
            this.postsList[listIndex] = { ...post }
          }
          if (this.currentPost?.id === entityId) {
            this.currentPost.isLikedByCurrentUser = isLiked
            this.currentPost.likeCount = likeCount
            if (updatedAt !== undefined) {
              this.currentPost.updated_at = updatedAt
            }
          }
          postCache.set(entityId, { ...post })
        }
      } else if (entityType === 'comment') {
        if (this.currentPost?.comments) {
          const commentIndex = this.currentPost.comments.findIndex((c) => c.id === entityId)
          if (commentIndex !== -1) {
            const updatedComments = [...this.currentPost.comments]
            updatedComments[commentIndex] = {
              ...updatedComments[commentIndex],
              isLikedByCurrentUser: isLiked,
              likeCount,
              ...(updatedAt !== undefined && { updated_at: updatedAt }),
            }
            this.currentPost = { ...this.currentPost, comments: updatedComments }
          }
        }
        const cachedComment = commentCache.get(entityId)
        if (cachedComment) {
          commentCache.set(entityId, {
            ...cachedComment,
            isLikedByCurrentUser: isLiked,
            likeCount,
            ...(updatedAt !== undefined && { updated_at: updatedAt }),
          })
        }
      } else if (entityType === 'reply' && commentId) {
        if (this.currentPost?.comments) {
          const commentIndex = this.currentPost.comments.findIndex((c) => c.id === commentId)
          if (commentIndex !== -1) {
            const comment = this.currentPost.comments[commentIndex]
            if (comment.replies) {
              const replyIndex = comment.replies.findIndex((r) => r.id === entityId)
              if (replyIndex !== -1) {
                const updatedReplies = [...comment.replies]
                updatedReplies[replyIndex] = {
                  ...updatedReplies[replyIndex],
                  isLikedByCurrentUser: isLiked,
                  likeCount,
                  ...(updatedAt !== undefined && { updated_at: updatedAt }),
                }
                const updatedComments = [...this.currentPost.comments]
                updatedComments[commentIndex] = { ...comment, replies: updatedReplies }
                this.currentPost = { ...this.currentPost, comments: updatedComments }
              }
            }
          }
        }
        const cachedReply = replyCache.get(entityId)
        if (cachedReply) {
          replyCache.set(entityId, {
            ...cachedReply,
            isLikedByCurrentUser: isLiked,
            likeCount,
            ...(updatedAt !== undefined && { updated_at: updatedAt }),
          })
        }
      }
    },

    /**
     * Updates post state in the store and caches.
     */
    updatePostState(post: PostPreviewResponse, userId: string) {
      this.postsMap[post.id] = post
      const listIndex = this.postsList.findIndex((p) => p.id === post.id)
      if (listIndex !== -1) {
        this.postsList[listIndex] = post
      } else if (post.isVisible) {
        this.postsList.unshift(post)
      }
      if (this.profilePosts[userId]) {
        if (post.isVisible && !this.profilePosts[userId].postIds.includes(post.id)) {
          this.profilePosts[userId].postIds.unshift(post.id)
        } else if (!post.isVisible) {
          this.profilePosts[userId].postIds = this.profilePosts[userId].postIds.filter(
            (id) => id !== post.id,
          )
        }
      }
      const authStore = useAuthStore()
      if (authStore.isAuthenticated && authStore.getUserId() === userId) {
        if (post.isVisible && !this.myPosts.postIds.includes(post.id)) {
          this.myPosts.postIds.unshift(post.id)
        } else if (!post.isVisible) {
          this.myPosts.postIds = this.myPosts.postIds.filter((id) => id !== post.id)
        }
      }
      postCache.set(post.id, post)
    },

    /**
     * Removes a post from state and caches.
     */
    removePostFromState(postId: string, userId: string) {
      delete this.postsMap[postId]
      this.postsList = this.postsList.filter((p) => p.id !== postId)
      if (this.profilePosts[userId]) {
        this.profilePosts[userId].postIds = this.profilePosts[userId].postIds.filter(
          (id) => id !== postId,
        )
      }
      const authStore = useAuthStore()
      if (authStore.isAuthenticated && authStore.getUserId() === userId) {
        this.myPosts.postIds = this.myPosts.postIds.filter((id) => id !== postId)
      }
      postCache.remove(postId)
      this.cleanupPostListener(postId)
    },

    /**
     * Updates comment state in the store and caches.
     */
    updateCommentState(comment: CommentDetailResponse | CommentPreviewResponse, postId: string) {
      const commentPreview = this.createCommentPreview(comment, postId)
      if (this.currentPost?.id === postId) {
        const comments = this.currentPost.comments || []
        const index = comments.findIndex((c) => c.id === comment.id)
        const commentDetail: CommentDetailResponse = {
          ...commentPreview,
          replies: index !== -1 ? comments[index].replies || [] : [],
        }
        const updatedComments = [...comments]
        if (index !== -1) {
          updatedComments[index] = commentDetail
        } else if (comment.isVisible) {
          updatedComments.push(commentDetail)
        }
        this.currentPost = { ...this.currentPost, comments: updatedComments }
      }

      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        try {
          const currentUserId = authStore.getUserId()
          if (comment.userId === currentUserId && comment.isVisible) {
            if (!this.myComments.commentIds.includes(comment.id)) {
              this.myComments.commentIds.unshift(comment.id)
            }
          } else if (comment.userId === currentUserId && !comment.isVisible) {
            this.myComments.commentIds = this.myComments.commentIds.filter(
              (id) => id !== comment.id,
            )
          }
        } catch (error) {
          console.error('Error updating user-specific comment state:', error)
        }
      }

      if (comment.isVisible) {
        commentCache.set(comment.id, commentPreview)
      }
    },

    /**
     * Removes a comment from state and caches.
     */
    removeCommentFromState(commentId: string, postId: string, userId?: string) {
      if (this.currentPost?.id === postId && this.currentPost.comments) {
        const updatedComments = this.currentPost.comments.filter((c) => c.id !== commentId)
        this.currentPost = {
          ...this.currentPost,
          comments: updatedComments,
          commentCount: Math.max(0, (this.currentPost.commentCount || 0) - 1),
        }
      }
      if (this.postsMap[postId]) {
        this.postsMap[postId] = {
          ...this.postsMap[postId],
          commentCount: Math.max(0, (this.postsMap[postId].commentCount || 0) - 1),
        }
      }
      const authStore = useAuthStore()
      const currentUserId = authStore.getUserId()
      if (userId === currentUserId) {
        this.myComments.commentIds = this.myComments.commentIds.filter((id) => id !== commentId)
      }
      commentCache.remove(commentId)
      this.cleanupCommentListener(commentId)
    },

    /**
     * Updates reply state in the store and caches.
     */
    updateReplyState(
      reply: ReplyDetailResponse | ReplyPreviewResponse,
      postId: string,
      commentId: string,
    ) {
      const replyPreview = this.createReplyPreview(reply, postId, commentId)
      if (this.currentPost?.id === postId && this.currentPost.comments) {
        const commentIndex = this.currentPost.comments.findIndex((c) => c.id === commentId)
        if (commentIndex !== -1) {
          const comment = this.currentPost.comments[commentIndex]
          const updatedReplies = comment.replies ? [...comment.replies] : []
          const replyIndex = updatedReplies.findIndex((r) => r.id === reply.id)
          if (replyIndex !== -1) {
            updatedReplies[replyIndex] = replyPreview
          } else if (reply.isVisible) {
            updatedReplies.push(replyPreview)
          }
          const updatedComments = [...this.currentPost.comments]
          updatedComments[commentIndex] = {
            ...comment,
            replies: updatedReplies,
            replyCount: updatedReplies.length,
          }
          this.currentPost = { ...this.currentPost, comments: updatedComments }
        }
      }

      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        try {
          const currentUserId = authStore.getUserId()
          if (reply.userId === currentUserId && reply.isVisible) {
            if (!this.myReplies.replyIds.includes(reply.id)) {
              this.myReplies.replyIds.unshift(reply.id)
            }
          } else if (reply.userId === currentUserId && !reply.isVisible) {
            this.myReplies.replyIds = this.myReplies.replyIds.filter((id) => id !== reply.id)
          }
        } catch (error) {
          console.error('Error updating user-specific reply state:', error)
        }
      }

      if (reply.isVisible) {
        replyCache.set(reply.id, replyPreview)
      }
    },

    /**
     * Removes a reply from state and caches.
     */
    removeReplyFromState(replyId: string, postId: string, commentId: string, userId?: string) {
      if (this.currentPost?.id === postId && this.currentPost.comments) {
        const commentIndex = this.currentPost.comments.findIndex((c) => c.id === commentId)
        if (commentIndex !== -1) {
          const comment = this.currentPost.comments[commentIndex]
          if (comment.replies) {
            const updatedReplies = comment.replies.filter((r) => r.id !== replyId)
            const updatedComments = [...this.currentPost.comments]
            updatedComments[commentIndex] = {
              ...comment,
              replies: updatedReplies,
              replyCount: updatedReplies.length,
            }
            this.currentPost = { ...this.currentPost, comments: updatedComments }
          }
        }
      }
      const authStore = useAuthStore()
      const currentUserId = authStore.getUserId()
      if (userId === currentUserId) {
        this.myReplies.replyIds = this.myReplies.replyIds.filter((id) => id !== replyId)
      }
      replyCache.remove(replyId)
      this.cleanupReplyListener(replyId)
    },

    /**
     * Updates appeal state in the store.
     */
    updateAppealState(appeal: AppealDetailResponse) {
      const appealIndex = this.appealsList.findIndex((a) => a.id === appeal.id)
      if (appealIndex !== -1) {
        this.appealsList[appealIndex] = appeal
      } else {
        this.appealsList.push(appeal)
      }

      if (appeal.status === 'pending') {
        const pendingIndex = this.pendingAppeals.findIndex((a) => a.id === appeal.id)
        if (pendingIndex === -1) {
          this.pendingAppeals.push(appeal)
        } else {
          this.pendingAppeals[pendingIndex] = appeal
        }
      } else {
        this.pendingAppeals = this.pendingAppeals.filter((a) => a.id !== appeal.id)
      }

      const authStore = useAuthStore()
      const currentUserId = authStore.getUserId()
      if (appeal.userId === currentUserId && !this.myAppeals.appealIds.includes(appeal.id)) {
        this.myAppeals.appealIds.unshift(appeal.id)
      }
    },

    /**
     * Dynamically adjusts cache size based on active users with an upper limit.
     */
    adjustCacheSize(activeUsers: number = 50) {
      const baseSize = 100
      const maxDynamicSize = 500
      const dynamicSize = Math.min(baseSize * (activeUsers / 1000), maxDynamicSize)
      postCache.resize(dynamicSize)
      commentCache.resize(dynamicSize * 2)
      replyCache.resize(dynamicSize * 3)
      console.log(
        `Cache resized: posts=${dynamicSize}, comments=${dynamicSize * 2}, replies=${dynamicSize * 3}`,
      )
    },

    /**
     * Periodically invalidates cache for stale data based on TTL and maxAge.
     */
    async invalidateCache() {
      postCache.cleanExpired()
      commentCache.cleanExpired()
      replyCache.cleanExpired()
      console.log('Cache invalidation completed')
    },

    /**
     * Fetches a user's profile.
     */
    async fetchUserProfile(userId: string) {
      if (this.profiles[userId]?.loading) return
      this.profiles[userId] = { data: null, loading: true, error: null }
      try {
        const response = await getUserProfileFunc({ userId })
        const userProfile = response.data as UserDetailResponse
        this.profiles[userId].data = userProfile
      } catch (err) {
        this.profiles[userId].error = String(err)
      } finally {
        this.profiles[userId].loading = false
      }
    },

    /**
     * Fetches posts for a user's profile.
     */
    async fetchUserProfilePosts(userId: string, limit: number = 10, startAfter?: string | null) {
      this.loading.posts = true
      try {
        const response = await getUserProfilePostsFunc({ userId, limit, startAfter })
        console.log('fetchUserProfilePosts response:', response.data)
        const { success, posts, nextStartAfter } = response.data as {
          success: boolean
          posts: PostPreviewResponse[]
          nextStartAfter: string | null
        }
        if (!success) throw new Error('Failed to fetch posts')

        posts.forEach((post) => {
          this.postsMap[post.id] = post
        })

        if (!this.profilePosts[userId]) {
          this.profilePosts[userId] = { postIds: [], nextStartAfter: null }
        }
        const postIds = posts.map((post) => post.id)
        this.profilePosts[userId].postIds = startAfter
          ? [...this.profilePosts[userId].postIds, ...postIds]
          : postIds
        this.profilePosts[userId].nextStartAfter = nextStartAfter
      } catch (err) {
        console.error('fetchUserProfilePosts error:', err)
        throw new Error(this.handleError(err, 'fetchUserProfilePosts'))
      } finally {
        this.loading.posts = false
      }
    },

    /**
     * Sets up listeners for visible posts only with internet check.
     */
    setupVisiblePostListeners(visiblePostIds: string[]) {
      if (!navigator.onLine) {
        console.warn('Offline: Skipping setup of post listeners')
        return
      }
      const activeListeners = new Set(
        postCache.keys().filter((id) => postCache.getEntry(id)?.unsubscribe),
      )
      visiblePostIds.forEach((postId) => {
        const post = this.postsMap[postId]
        if (post && !activeListeners.has(postId)) {
          this.setupPostListener(post.userId, postId, post)
        }
      })
      Object.keys(this.postsMap).forEach((postId) => {
        if (!visiblePostIds.includes(postId) && activeListeners.has(postId)) {
          this.cleanupPostListener(postId)
        }
      })
      this.logActiveListeners()
    },

    /**
     * Creates a new post.
     */
    async addPost(request: CreatePostRequest): Promise<PostDetailResponse> {
      if (!request.title || !request.content) {
        throw new Error('Title and content are required')
      }
      this.loading.posts = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await createPostFunc({ ...request, userId })
        const newPost = response.data as PostDetailResponse
        const postPreview = this.createPostPreview(newPost)
        this.updatePostState(postPreview, userId)
        this.setupPostListener(userId, newPost.id, postPreview)
        this.adjustCacheSize()
        return newPost
      } catch (error) {
        throw new Error(this.handleError(error, 'addPost'))
      } finally {
        this.loading.posts = false
      }
    },

    /**
     * Sets up a Firestore listener for a post with logging and error handling.
     */
    setupPostListener(
      userId: string,
      postId: string,
      initialPost: PostPreviewResponse,
    ): (() => void) | null {
      if (!navigator.onLine) {
        console.warn(`Offline: Skipping listener for post ${postId}`)
        return null
      }
      this.cleanupPostListener(postId)
      console.log(`Setting up listener for post ${postId}`)
      const postRef = doc(firestore, `users/${userId}/posts/${postId}`)
      const unsubscribe = onSnapshot(
        postRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as PostDataRead
            if (!data.title || !data.content || !data.authorType) {
              console.warn(`Invalid post data for ${postId}: missing required fields`)
              return
            }
            const updatedPost: PostPreviewResponse = {
              id: postId,
              userId,
              created_at: data.created_at.toDate().toISOString(),
              updated_at: data.updated_at?.toDate().toISOString() || null,
              isVisible: data.isVisible,
              title: data.title,
              content: data.content,
              authorType: data.authorType,
              authorName: data.authorName,
              commentCount: data.commentCount,
              likeCount: data.likeCount,
              reportsReceived: data.reportsReceived,
              appealStatus: data.appealStatus || 'none',
              isLikedByCurrentUser: data.isLikedByCurrentUser,
              path: this.generatePath(EntityType.Post, postId, userId),
            }
            this.updatePostState(updatedPost, userId)
            postCache.set(postId, updatedPost, unsubscribe)
            console.debug(`Post ${postId} updated in cache via listener`)
          } else {
            this.removePostFromState(postId, userId)
            postCache.remove(postId)
            console.debug(`Post ${postId} removed from cache (no longer exists)`)
          }
        },
        (error) => {
          console.error(`Error in post listener for ${postId}:`, error)
          postCache.remove(postId)
        },
      )
      postCache.set(postId, initialPost, unsubscribe)
      this.logActiveListeners()
      return unsubscribe
    },

    /**
     * Cleans up a post listener with logging.
     */
    cleanupPostListener(postId: string) {
      const entry = postCache.getEntry(postId)
      if (entry?.unsubscribe) {
        entry.unsubscribe()
        postCache.remove(postId)
        console.log(`Cleaned up listener for post ${postId}`)
      }
    },

    /**
     * Updates an existing post.
     */
    async updatePost(request: UpdatePostRequest): Promise<PostDetailResponse> {
      if (!request.postId || (!request.title && !request.content)) {
        throw new Error('postId and at least one of title or content are required')
      }
      this.loading.posts = true
      try {
        const response = await updatePostFunc(request)
        const updatedPost = response.data as PostDetailResponse
        const postPreview = this.createPostPreview(updatedPost)
        this.updatePostState(postPreview, updatedPost.userId)
        if (this.currentPost?.id === request.postId) {
          this.currentPost = { ...updatedPost, comments: this.currentPost.comments || [] }
        }
        if (!postCache.getEntry(updatedPost.id)?.unsubscribe) {
          this.setupPostListener(updatedPost.userId, updatedPost.id, postPreview)
        }
        return updatedPost
      } catch (error) {
        throw new Error(this.handleError(error, 'updatePost'))
      } finally {
        this.loading.posts = false
      }
    },

    /**
     * Deletes a post.
     */
    async deletePost(request: DeletePostRequest): Promise<void> {
      if (!request.postId || !request.postOwnerId) {
        throw new Error('postId and postOwnerId are required')
      }
      this.loading.posts = true
      try {
        const userId = await this.ensureAuthenticated()
        if (userId !== request.postOwnerId) {
          throw new Error('Unauthorized: You can only delete your own posts')
        }
        const response = await deletePostFunc(request)
        const { success } = response.data as { success: boolean }
        if (success) {
          this.removePostFromState(request.postId, request.postOwnerId)
          if (this.currentPost?.id === request.postId) {
            this.currentPost = null
          }
          this.adjustCacheSize()
        } else {
          throw new Error('Failed to delete post')
        }
      } catch (error) {
        throw new Error(this.handleError(error, 'deletePost'))
      } finally {
        this.loading.posts = false
      }
    },

    /**
     * Fetches posts sorted by likes or creation time.
     */
    async fetchPostsByLikes(
      limit: number = 10,
      startAfter?: string | [number, string],
      sortBy: 'created_at' | 'likeCount' = 'created_at',
      timePeriod: 'all' | 'daily' | 'weekly' | 'monthly' = 'all',
    ): Promise<PostPreviewResponse[]> {
      if (limit <= 0 || limit > 100) {
        throw new Error('Limit must be between 1 and 100')
      }
      this.loading.posts = true
      try {
        const safeStartAfter: string | [number, string] | undefined = startAfter
        if (startAfter !== undefined) {
          if (sortBy === 'created_at' && typeof startAfter !== 'string') {
            throw new Error('startAfter must be a string for sortBy "created_at"')
          }
          if (sortBy === 'likeCount' && (!Array.isArray(startAfter) || startAfter.length !== 2)) {
            throw new Error('startAfter must be a [number, string] array for sortBy "likeCount"')
          }
        }

        const response = await getPostsByLikesFunc({
          limit,
          startAfterCursor: safeStartAfter,
          sortBy,
          timePeriod,
        })

        const { posts, nextStartAfter } = response.data as {
          posts: PostPreviewResponse[]
          nextStartAfter: string | [number, string] | null
        }
        const existingIds = new Set(this.postsList.map((p) => p.id))
        const newPosts = posts.filter((p) => !existingIds.has(p.id))
        newPosts.forEach((post) => {
          this.postsMap[post.id] = post
        })
        if (startAfter) {
          this.postsList = [...this.postsList, ...newPosts]
        } else {
          this.postsList = newPosts
        }
        this.nextPostPageToken = nextStartAfter || null
        this.hasMorePosts = !!nextStartAfter && posts.length >= limit
        this.setupVisiblePostListeners(newPosts.map((p) => p.id))
        this.adjustCacheSize()
        return posts
      } catch (error) {
        throw new Error(this.handleError(error, 'fetchPostsByLikes'))
      } finally {
        this.loading.posts = false
      }
    },

    /**
     * Fetches the authenticated user's posts.
     */
    async fetchMyPosts(
      limit: number = 10,
      startAfter?: string | null,
    ): Promise<PostPreviewResponse[]> {
      if (limit <= 0 || limit > 100) throw new Error('Limit must be between 1 and 100')
      this.loading.posts = true
      try {
        const userId = await this.ensureAuthenticated()
        const batchSize = 5
        const batches = Math.ceil(limit / batchSize)
        const posts: PostPreviewResponse[] = []
        let currentStartAfter = startAfter ?? null

        for (let i = 0; i < batches; i++) {
          const batchLimit = Math.min(batchSize, limit - posts.length)
          const response = await getPostsByUserFunc({
            userId,
            limit: batchLimit,
            startAfter: currentStartAfter,
          })
          const {
            success,
            posts: batchPosts,
            nextStartAfter,
          } = response.data as {
            success: boolean
            posts: PostPreviewResponse[]
            nextStartAfter: string | null
          }
          if (!success) throw new Error('Failed to fetch my posts')
          posts.push(...batchPosts)
          currentStartAfter = nextStartAfter
          if (!nextStartAfter || posts.length >= limit) break
        }

        const existingIds = new Set(this.postsMap ? Object.keys(this.postsMap) : [])
        posts.forEach((post) => {
          if (!existingIds.has(post.id)) {
            this.postsMap[post.id] = post
          }
        })

        const postIds = posts
          .map((post) => post.id)
          .filter((id) => !this.myPosts.postIds.includes(id))
        if (startAfter) {
          this.myPosts.postIds = [...this.myPosts.postIds, ...postIds]
        } else {
          this.myPosts.postIds = postIds
        }
        this.myPosts.nextStartAfter = currentStartAfter
        this.setupVisiblePostListeners(postIds)
        this.adjustCacheSize()
        return posts
      } catch (error) {
        throw new Error(this.handleError(error, 'fetchMyPosts'))
      } finally {
        this.loading.posts = false
      }
    },

    /**
     * Sets up a Firestore listener for a comment with logging and error handling.
     */
    setupCommentListener(
      postId: string,
      commentId: string,
      initialComment: CommentDetailResponse | CommentPreviewResponse,
    ): (() => void) | undefined {
      if (!navigator.onLine) {
        console.warn(`Offline: Skipping listener for comment ${commentId}`)
        return undefined
      }
      this.cleanupCommentListener(commentId)
      console.log(`Setting up listener for comment ${commentId}`)
      const commentRef = doc(
        firestore,
        `users/${initialComment.postOwnerId}/posts/${postId}/comments/${commentId}`,
      )
      const commentPreview = this.createCommentPreview(initialComment, postId)
      const unsubscribe = onSnapshot(
        commentRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as CommentDataRead
            if (!data.content || !data.authorType) {
              console.warn(`Invalid comment data for ${commentId}`, data)
              return
            }
            const updatedComment: CommentPreviewResponse = {
              postOwnerId: data.postOwnerId,
              id: commentId,
              postId,
              userId: data.userId,
              created_at: data.created_at.toDate().toISOString(),
              updated_at: data.updated_at?.toDate().toISOString() || null,
              isVisible: data.isVisible,
              content: data.content,
              authorType: data.authorType,
              authorName: data.authorName,
              replyCount: data.replyCount,
              likeCount: data.likeCount,
              reportsReceived: data.reportsReceived,
              appealStatus: data.appealStatus || 'none',
              isLikedByCurrentUser: data.isLikedByCurrentUser,
              path: this.generatePath(EntityType.Comment, commentId, postId, data.postOwnerId),
            }
            this.updateCommentState(updatedComment, postId)
            commentCache.set(commentId, updatedComment, unsubscribe)
            console.debug(`Comment ${commentId} updated in cache via listener`)
          } else {
            this.removeCommentFromState(commentId, postId, initialComment.userId)
            commentCache.remove(commentId)
            console.debug(`Comment ${commentId} removed from cache (no longer exists)`)
          }
        },
        (error) => {
          console.error(`Error in comment listener for ${commentId}:`, error)
          commentCache.remove(commentId)
        },
      )
      commentCache.set(commentId, commentPreview, unsubscribe)
      this.logActiveListeners()
      return unsubscribe
    },

    /**
     * Cleans up a comment listener with logging.
     */
    cleanupCommentListener(commentId: string) {
      const entry = commentCache.getEntry(commentId)
      if (entry?.unsubscribe) {
        entry.unsubscribe()
        commentCache.remove(commentId)
        console.log(`Cleaned up listener for comment ${commentId}`)
      }
    },

    /**
     * Adds a new comment.
     */
    async addComment(request: AddCommentRequest): Promise<CommentDetailResponse> {
      if (!request.postId || !request.content) {
        throw new Error('postId and content are required')
      }
      this.loading.comments = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await addCommentFunc({ ...request, userId })
        const newComment = response.data as CommentDetailResponse
        this.updateCommentState(newComment, request.postId)
        if (this.postsMap[request.postId]) {
          this.postsMap[request.postId].commentCount =
            (this.postsMap[request.postId].commentCount || 0) + 1
        }
        this.setupCommentListener(request.postId, newComment.id, newComment)
        this.adjustCacheSize() // Adjust cache size after adding comment
        return newComment
      } catch (error) {
        throw new Error(this.handleError(error, 'addComment'))
      } finally {
        this.loading.comments = false
      }
    },

    /**
     * Updates an existing comment.
     */
    async updateComment(request: UpdateCommentRequest): Promise<CommentDetailResponse> {
      if (!request.commentId || !request.postId || !request.content) {
        throw new Error('commentId, postId, and content are required')
      }
      this.loading.comments = true
      try {
        const response = await updateCommentFunc(request)
        const updatedComment = response.data as CommentDetailResponse
        this.updateCommentState(updatedComment, request.postId)
        if (!commentCache.getEntry(updatedComment.id)?.unsubscribe) {
          this.setupCommentListener(request.postId, updatedComment.id, updatedComment)
        }
        return updatedComment
      } catch (error) {
        throw new Error(this.handleError(error, 'updateComment'))
      } finally {
        this.loading.comments = false
      }
    },

    /**
     * Deletes a comment.
     */
    async deleteComment(request: DeleteCommentRequest): Promise<void> {
      if (!request.commentId || !request.postId) {
        throw new Error('commentId and postId are required')
      }
      this.loading.comments = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await deleteCommentFunc(request)
        const { success } = response.data as { success: boolean }
        if (success) {
          this.removeCommentFromState(request.commentId, request.postId, userId)
          this.adjustCacheSize() // Adjust cache size after deletion
        } else {
          throw new Error('Failed to delete comment')
        }
      } catch (error) {
        throw new Error(this.handleError(error, 'deleteComment'))
      } finally {
        this.loading.comments = false
      }
    },

    /**
     * Fetches the authenticated user's comments.
     */
    async fetchMyComments(
      limit: number = 10,
      startAfter?: string,
    ): Promise<CommentPreviewResponse[]> {
      if (limit <= 0 || limit > 100) {
        throw new Error('Limit must be between 1 and 100')
      }
      this.loading.comments = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await getCommentsByUserFunc({ userId, limit, startAfter })
        const { success, comments, nextStartAfter } = response.data as {
          success: boolean
          comments: CommentPreviewResponse[]
          nextStartAfter: string | null
        }
        if (!success) throw new Error('Failed to fetch comments')

        const existingIds = new Set(this.myComments.commentIds)
        const newComments = comments.filter((c) => !existingIds.has(c.id))
        newComments.forEach((comment) => {
          this.updateCommentState(comment, comment.postId)
          if (!commentCache.getEntry(comment.id)?.unsubscribe) {
            this.setupCommentListener(comment.postId, comment.id, comment)
          }
        })

        const commentIds = newComments.map((comment) => comment.id)
        this.myComments.commentIds = startAfter
          ? [...this.myComments.commentIds, ...commentIds]
          : commentIds
        this.myComments.nextStartAfter = nextStartAfter
        this.adjustCacheSize()

        return comments
      } catch (error) {
        throw new Error(this.handleError(error, 'fetchMyComments'))
      } finally {
        this.loading.comments = false
      }
    },

    /**
     * Sets up a Firestore listener for a reply with logging and error handling.
     */
    setupReplyListener(
      postId: string,
      commentId: string,
      replyId: string,
      initialReply: ReplyDetailResponse | ReplyPreviewResponse,
    ): (() => void) | undefined {
      if (!navigator.onLine) {
        console.warn(`Offline: Skipping listener for reply ${replyId}`)
        return undefined
      }
      this.cleanupReplyListener(replyId)
      console.log(`Setting up listener for reply ${replyId}`)
      const replyRef = doc(
        firestore,
        `users/${initialReply.postOwnerId}/posts/${postId}/comments/${commentId}/replies/${replyId}`,
      )
      const replyPreview = this.createReplyPreview(initialReply, postId, commentId)
      const unsubscribe = onSnapshot(
        replyRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as ReplyDataRead
            if (!data.content || !data.authorType) {
              console.warn(`Invalid reply data for ${replyId}`, data)
              return
            }
            const updatedReply: ReplyPreviewResponse = {
              postOwnerId: data.postOwnerId,
              id: replyId,
              postId,
              commentId,
              userId: data.userId,
              created_at: data.created_at.toDate().toISOString(),
              updated_at: data.updated_at?.toDate().toISOString() || null,
              isVisible: data.isVisible,
              content: data.content,
              authorType: data.authorType,
              authorName: data.authorName,
              likeCount: data.likeCount,
              reportsReceived: data.reportsReceived,
              appealStatus: data.appealStatus || 'none',
              isLikedByCurrentUser: data.isLikedByCurrentUser,
              path: this.generatePath(
                EntityType.Reply,
                replyId,
                commentId,
                postId,
                data.postOwnerId,
              ),
            }
            this.updateReplyState(updatedReply, postId, commentId)
            replyCache.set(replyId, updatedReply, unsubscribe)
            console.debug(`Reply ${replyId} updated in cache via listener`)
          } else {
            this.removeReplyFromState(replyId, postId, commentId, initialReply.userId)
            replyCache.remove(replyId)
            console.debug(`Reply ${replyId} removed from cache (no longer exists)`)
          }
        },
        (error) => {
          console.error(`Error in reply listener for ${replyId}:`, error)
          replyCache.remove(replyId)
        },
      )
      replyCache.set(replyId, replyPreview, unsubscribe)
      this.logActiveListeners()
      return unsubscribe
    },

    /**
     * Cleans up a reply listener with logging.
     */
    cleanupReplyListener(replyId: string) {
      const entry = replyCache.getEntry(replyId)
      if (entry?.unsubscribe) {
        entry.unsubscribe()
        replyCache.remove(replyId)
        console.log(`Cleaned up listener for reply ${replyId}`)
      }
    },

    /**
     * Adds a new reply.
     */
    async addReply(request: AddReplyRequest): Promise<ReplyDetailResponse> {
      if (!request.postId || !request.commentId || !request.content) {
        throw new Error('postId, commentId, and content are required')
      }
      this.loading.replies = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await addReplyFunc({ ...request, userId })
        const newReply = response.data as ReplyDetailResponse
        this.updateReplyState(newReply, request.postId, request.commentId)
        this.setupReplyListener(request.postId, request.commentId, newReply.id, newReply)
        this.adjustCacheSize()
        return newReply
      } catch (error) {
        throw new Error(this.handleError(error, 'addReply'))
      } finally {
        this.loading.replies = false
      }
    },

    /**
     * Updates an existing reply.
     */
    async updateReply(request: UpdateReplyRequest): Promise<ReplyDetailResponse> {
      if (!request.replyId || !request.postId || !request.commentId || !request.content) {
        throw new Error('replyId, postId, commentId, and content are required')
      }
      this.loading.replies = true
      try {
        const response = await updateReplyFunc(request)
        const updatedReply = response.data as ReplyDetailResponse
        this.updateReplyState(updatedReply, request.postId, request.commentId)
        if (!replyCache.getEntry(updatedReply.id)?.unsubscribe) {
          this.setupReplyListener(request.postId, request.commentId, updatedReply.id, updatedReply)
        }
        return updatedReply
      } catch (error) {
        throw new Error(this.handleError(error, 'updateReply'))
      } finally {
        this.loading.replies = false
      }
    },

    /**
     * Deletes a reply.
     */
    async deleteReply(request: DeleteReplyRequest): Promise<void> {
      if (!request.replyId || !request.postId || !request.commentId) {
        throw new Error('replyId, postId, and commentId are required')
      }
      this.loading.replies = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await deleteReplyFunc(request)
        const { success } = response.data as { success: boolean }
        if (success) {
          this.removeReplyFromState(request.replyId, request.postId, request.commentId, userId)
          this.adjustCacheSize()
        } else {
          throw new Error('Failed to delete reply')
        }
      } catch (error) {
        throw new Error(this.handleError(error, 'deleteReply'))
      } finally {
        this.loading.replies = false
      }
    },

    /**
     * Fetches the authenticated user's replies.
     */
    async fetchMyReplies(limit: number = 10, startAfter?: string): Promise<ReplyPreviewResponse[]> {
      if (limit <= 0 || limit > 100) {
        throw new Error('Limit must be between 1 and 100')
      }
      this.loading.replies = true
      try {
        const userId = await this.ensureAuthenticated()
        const response = await getRepliesByUserFunc({ userId, limit, startAfter })
        const { success, replies, nextStartAfter } = response.data as {
          success: boolean
          replies: ReplyPreviewResponse[]
          nextStartAfter: string | null
        }
        if (!success) throw new Error('Failed to fetch replies')

        const existingIds = new Set(this.myReplies.replyIds)
        const newReplies = replies.filter((r) => !existingIds.has(r.id))
        newReplies.forEach((reply) => {
          this.updateReplyState(reply, reply.postId, reply.commentId)
          if (!replyCache.getEntry(reply.id)?.unsubscribe) {
            this.setupReplyListener(reply.postId, reply.commentId, reply.id, reply)
          }
        })

        const replyIds = newReplies.map((reply) => reply.id)
        this.myReplies.replyIds = startAfter ? [...this.myReplies.replyIds, ...replyIds] : replyIds
        this.myReplies.nextStartAfter = nextStartAfter
        this.adjustCacheSize()

        return replies
      } catch (error) {
        throw new Error(this.handleError(error, 'fetchMyReplies'))
      } finally {
        this.loading.replies = false
      }
    },

    /**
     * Cleans up all listeners associated with a given post, including comments and replies.
     * @param postId The ID of the post whose listeners should be cleaned up.
     */
    cleanupPostRelatedListeners(postId: string) {
      this.cleanupPostListener(postId)
      commentCache.keys().forEach((commentId) => {
        const comment = commentCache.get(commentId)
        if (comment?.postId === postId) {
          this.cleanupCommentListener(commentId)
        }
      })
      replyCache.keys().forEach((replyId) => {
        const reply = replyCache.get(replyId)
        if (reply?.postId === postId) {
          this.cleanupReplyListener(replyId)
        }
      })
      console.log(`Cleaned up all listeners for post ${postId}`)
    },

    /**
     * Cleans up all listeners and resets caches.
     */
    cleanupAllListeners() {
      postCache.clear()
      commentCache.clear()
      replyCache.clear()
      this.postsMap = {}
      this.postsList = []
      this.currentPost = null
      this.myPosts = { postIds: [], nextStartAfter: null }
      this.myComments = { commentIds: [], nextStartAfter: null }
      this.myReplies = { replyIds: [], nextStartAfter: null }
      this.profilePosts = {}
      this.appealsList = []
      this.pendingReports = []
      this.pendingAppeals = []
      this.myAppeals = { appealIds: [], nextStartAfter: null }
      console.log('All listeners and caches cleared')
    },

    /**
     * Initializes periodic cache invalidation.
     */
    initializeCacheInvalidation() {
      setInterval(
        () => {
          this.invalidateCache().catch((error) => {
            console.error('Cache invalidation failed:', error)
          })
        },
        10 * 60 * 1000,
      )
    },

    /**
     * Logs out the user and cleans up listeners.
     */
    logout() {
      this.cleanupAllListeners()
      const authStore = useAuthStore()
      authStore.logout()
    },

    /**
     * Fetches detailed post information.
     */
    async fetchPostDetails(
      postOwnerId: string,
      postId: string,
      includeComments: boolean = true,
      includeReplies: boolean = true,
      commentLimit: number = 10,
      replyLimit: number = 5,
      commentStartAfter?: string,
      replyStartAfter?: { commentId: string; replyId: string }[],
    ): Promise<PostDetailResponse> {
      if (!postOwnerId || !postId) {
        throw new Error('postOwnerId and postId are required')
      }
      if (commentLimit <= 0 || commentLimit > 50) {
        throw new Error('commentLimit must be between 1 and 50')
      }
      if (replyLimit <= 0 || replyLimit > 50) {
        throw new Error('replyLimit must be between 1 and 50')
      }

      this.loading.postDetails = true

      try {
        const lastReplyIds: Record<string, string> = {}
        if (replyStartAfter && includeReplies && Array.isArray(replyStartAfter)) {
          replyStartAfter.forEach(({ commentId, replyId }) => {
            lastReplyIds[commentId] = replyId
          })
        }

        const response = await getPostDetailFunc({
          postOwnerId,
          postId,
          commentPageSize: includeComments ? commentLimit : 0,
          lastCommentId: commentStartAfter || undefined,
          replyPageSize: includeReplies ? replyLimit : 0,
          lastReplyIds,
        })

        const { post, pagination } = response.data as {
          post: PostDetailResponse
          pagination: {
            nextCommentId?: string
            hasMoreComments: boolean
            nextReplyIds?: Record<string, string>
            hasMoreReplies?: Record<string, boolean>
          }
        }
        if (!post) {
          throw new Error('Invalid response: post data missing')
        }

        let updatedComments: CommentDetailResponse[] = []
        if (includeComments && post.comments) {
          if (!commentStartAfter || !this.currentPost) {
            updatedComments = post.comments || []
          } else {
            const existingComments = this.currentPost.comments || []
            const existingCommentIds = new Set(existingComments.map((comment) => comment.id))
            updatedComments = [
              ...existingComments,
              ...(post.comments || []).filter((comment) => !existingCommentIds.has(comment.id)),
            ]
          }

          if (includeReplies) {
            updatedComments = updatedComments.map((comment) => {
              const newReplies = comment.replies || []
              const existingComment = this.currentPost?.comments?.find((c) => c.id === comment.id)
              const existingReplies = existingComment?.replies || []
              const existingReplyIds = new Set(existingReplies.map((reply) => reply.id))
              const mergedReplies = [
                ...existingReplies,
                ...newReplies.filter((reply) => !existingReplyIds.has(reply.id)),
              ]
              return { ...comment, replies: mergedReplies }
            })
          }
        } else {
          updatedComments = this.currentPost?.comments || []
        }

        this.currentPost = {
          ...(this.currentPost || {}),
          ...post,
          comments: updatedComments,
        }

        this.nextCommentPageToken = pagination.nextCommentId || null

        if (pagination.nextReplyIds) {
          Object.entries(pagination.nextReplyIds).forEach(([commentId, nextReplyId]) => {
            this.nextReplyPageToken[commentId] = nextReplyId || null
          })
        }

        updatedComments.forEach((comment) => {
          if (comment.replies && comment.isVisible) {
            this.setupVisibleReplyListeners(
              postId,
              comment.id,
              comment.replies.map((r) => r.id),
            )
          }
        })

        if (!postCache.getEntry(postId)?.unsubscribe) {
          const postPreview = this.createPostPreview(post)
          this.setupPostListener(postOwnerId, postId, postPreview)
        }

        this.adjustCacheSize()

        return post
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        throw new Error(`Failed to fetch post details: ${errorMessage}`)
      } finally {
        this.loading.postDetails = false
      }
    },

    /**
     * Logs active Firestore listeners for debugging.
     */
    logActiveListeners() {
      const postListeners = Array.from(postCache.entries()).filter(
        ([, entry]) => !!entry?.unsubscribe,
      ).length
      const commentListeners = Array.from(commentCache.entries()).filter(
        ([, entry]) => !!entry?.unsubscribe,
      ).length
      const replyListeners = Array.from(replyCache.entries()).filter(
        ([, entry]) => !!entry?.unsubscribe,
      ).length
      console.log(
        `Active listeners: posts=${postListeners}, comments=${commentListeners}, replies=${replyListeners}`,
      )
    },

    /**
     * Toggles like for a post.
     */
    async togglePostLike(request: TogglePostLikeRequest): Promise<ToggleLikeResponse> {
      if (!request.postId) throw new Error('postId is required')
      this.loading.likes = true
      try {
        const response = await togglePostLikeFunc(request)
        const { success, isLiked, likeCount, updatedAt } = response.data as ToggleLikeResponse
        if (!success) throw new Error('Failed to toggle post like')
        this.updateLikeState('post', request.postId, isLiked, likeCount, updatedAt)
        return { success, isLiked, likeCount, updatedAt }
      } catch (error) {
        throw new Error(this.handleError(error, 'togglePostLike'))
      } finally {
        this.loading.likes = false
      }
    },

    /**
     * Toggles like for a comment.
     */
    async toggleCommentLike(request: ToggleCommentLikeRequest): Promise<ToggleLikeResponse> {
      if (!request.commentId || !request.postId) {
        throw new Error('commentId and postId are required')
      }
      this.loading.likes = true
      try {
        const response = await toggleCommentLikeFunc(request)
        const { success, isLiked, likeCount, updatedAt } = response.data as ToggleLikeResponse
        if (!success) throw new Error('Failed to toggle comment like')
        this.updateLikeState('comment', request.commentId, isLiked, likeCount, updatedAt)
        return { success, isLiked, likeCount, updatedAt }
      } catch (error) {
        throw new Error(this.handleError(error, 'toggleCommentLike'))
      } finally {
        this.loading.likes = false
      }
    },

    /**
     * Toggles like for a reply.
     */
    async toggleReplyLike(request: ToggleReplyLikeRequest): Promise<ToggleLikeResponse> {
      if (!request.replyId || !request.postId || !request.commentId) {
        throw new Error('replyId, postId, and commentId are required')
      }
      this.loading.likes = true
      try {
        const response = await toggleReplyLikeFunc(request)
        const { success, isLiked, likeCount, updatedAt } = response.data as ToggleLikeResponse
        if (!success) throw new Error('Failed to toggle reply like')
        this.updateLikeState(
          'reply',
          request.replyId,
          isLiked,
          likeCount,
          updatedAt,
          request.commentId,
        )
        return { success, isLiked, likeCount, updatedAt }
      } catch (error) {
        throw new Error(this.handleError(error, 'toggleReplyLike'))
      } finally {
        this.loading.likes = false
      }
    },

    /**
     * Reports a user.
     */
    async reportUser(request: UserReportRequest): Promise<ReportDetailResponse> {
      if (!request.reportedUserId || !request.reason) {
        throw new Error('targetUserId and reason are required')
      }
      this.loading.reports = true
      try {
        const response = await reportUserFunc(request)
        return response.data as ReportDetailResponse
      } catch (error) {
        throw new Error(this.handleError(error, 'reportUser'))
      } finally {
        this.loading.reports = false
      }
    },

    /**
     * Reports a post.
     */
    async reportPost(request: PostReportRequest): Promise<ReportDetailResponse> {
      if (!request.postId || !request.reason) {
        throw new Error('postId and reason are required')
      }
      this.loading.reports = true
      try {
        const response = await reportPostFunc(request)
        return response.data as ReportDetailResponse
      } catch (error) {
        throw new Error(this.handleError(error, 'reportPost'))
      } finally {
        this.loading.reports = false
      }
    },

    /**
     * Reports a comment.
     */
    async reportComment(request: CommentReportRequest): Promise<ReportDetailResponse> {
      if (!request.commentId || !request.postId || !request.reason) {
        throw new Error('commentId, postId, and reason are required')
      }
      this.loading.reports = true
      try {
        const response = await reportCommentFunc(request)
        return response.data as ReportDetailResponse
      } catch (error) {
        throw new Error(this.handleError(error, 'reportComment'))
      } finally {
        this.loading.reports = false
      }
    },

    /**
     * Reports a reply.
     */
    async reportReply(request: ReplyReportRequest): Promise<ReportDetailResponse> {
      if (!request.replyId || !request.postId || !request.commentId || !request.reason) {
        throw new Error('replyId, postId, commentId, and reason are required')
      }
      this.loading.reports = true
      try {
        const response = await reportReplyFunc(request)
        return response.data as ReportDetailResponse
      } catch (error) {
        throw new Error(this.handleError(error, 'reportReply'))
      } finally {
        this.loading.reports = false
      }
    },

    /**
     * Manages a report (admin only).
     */
    async manageReport(request: {
      path: string
      action: 'hideContent' | 'restrictUser' | 'dismiss'
      details?: string
    }): Promise<ReportDetailResponse> {
      if (!request.path || !['hideContent', 'restrictUser', 'dismiss'].includes(request.action)) {
        throw new Error(
          '유효한 path와 action (hideContent, restrictUser, dismiss 중 하나)이 필요합니다.',
        )
      }
      this.loading.reports = true
      try {
        const authStore = useAuthStore()
        if (!authStore.isAdmin) {
          throw new Error('관리자만 신고를 관리할 수 있습니다.')
        }
        const response = await manageReportFunc(request)
        const result = response.data as { success: boolean; message: string }
        if (!result.success) {
          throw new Error(result.message || '신고 처리에 실패했습니다.')
        }
        const reportRef = doc(firestore, request.path)
        const reportSnapshot = await getDoc(reportRef)
        if (!reportSnapshot.exists()) {
          throw new Error('Report not found')
        }
        const reportData = reportSnapshot.data() as ReportDataRead
        const updatedReport: ReportDetailResponse = {
          id: request.path.split('/').pop() || '',
          userId: reportData.userId || '',
          created_at: reportData.created_at?.toDate().toISOString() || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          contentRef: reportData.contentRef?.path || '',
          contentType: reportData.contentType,
          contentId: reportData.contentId || '',
          reason: reportData.reason ?? ReportReason.Other,
          status: 'resolved',
          actionTaken:
            request.action === 'hideContent'
              ? 'contentHidden'
              : request.action === 'restrictUser'
                ? 'userRestricted'
                : 'dismissed',
          actionDetails: request.details || null,
          path: request.path,
        }
        if (updatedReport.status === 'resolved') {
          this.pendingReports = this.pendingReports.filter((r) => r.path !== request.path)
        }
        console.log('신고가 성공적으로 처리되었습니다:', updatedReport)
        return updatedReport
      } catch (error) {
        throw new Error(this.handleError(error, 'manageReport'))
      } finally {
        this.loading.reports = false
      }
    },

    /**
     * Submits an appeal for a user.
     */
    async submitUserAppeal(request: UserAppealRequest): Promise<AppealDetailResponse> {
      this.loading.appeals = true
      try {
        this.validateAppealRequest(request, 'user')
        const response = await submitUserAppealFunc(request)
        const newAppeal = response.data as AppealDetailResponse
        this.updateAppealState(newAppeal)
        return newAppeal
      } catch (error) {
        throw new Error(this.handleError(error, 'submitUserAppeal'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Submits an appeal for a post.
     */
    async submitPostAppeal(request: PostAppealRequest): Promise<AppealDetailResponse> {
      this.loading.appeals = true
      try {
        this.validateAppealRequest(request, 'post')
        const response = await submitPostAppealFunc(request)
        const newAppeal = response.data as AppealDetailResponse
        this.updateAppealState(newAppeal)
        return newAppeal
      } catch (error) {
        throw new Error(this.handleError(error, 'submitPostAppeal'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Submits an appeal for a comment.
     */
    async submitCommentAppeal(request: CommentAppealRequest): Promise<AppealDetailResponse> {
      this.loading.appeals = true
      try {
        this.validateAppealRequest(request, 'comment')
        const response = await submitCommentAppealFunc(request)
        const newAppeal = response.data as AppealDetailResponse
        this.updateAppealState(newAppeal)
        return newAppeal
      } catch (error) {
        throw new Error(this.handleError(error, 'submitCommentAppeal'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Submits an appeal for a reply.
     */
    async submitReplyAppeal(request: ReplyAppealRequest): Promise<AppealDetailResponse> {
      this.loading.appeals = true
      try {
        this.validateAppealRequest(request, 'reply')
        const response = await submitReplyAppealFunc(request)
        const newAppeal = response.data as AppealDetailResponse
        this.updateAppealState(newAppeal)
        return newAppeal
      } catch (error) {
        throw new Error(this.handleError(error, 'submitReplyAppeal'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Reviews an appeal (admin only).
     */
    async reviewAppeal(
      userId: string,
      appealId: string,
      decision: 'approved' | 'rejected',
      rejectionReason?: string,
    ): Promise<AppealDetailResponse> {
      if (typeof userId !== 'string' || !userId.trim()) {
        throw new Error('Invalid userId: must be a non-empty string')
      }
      if (typeof appealId !== 'string' || !appealId.trim()) {
        throw new Error('Invalid appealId: must be a non-empty string')
      }
      if (!['approved', 'rejected'].includes(decision)) {
        throw new Error('Invalid decision: must be "approved" or "rejected"')
      }
      if (decision === 'rejected' && (!rejectionReason || !rejectionReason.trim())) {
        throw new Error('Invalid rejectionReason: must be a non-empty string for rejected appeals')
      }
      this.loading.appeals = true
      try {
        const authStore = useAuthStore()
        if (!authStore.isAdmin) {
          throw new Error('Only admins can review appeals')
        }
        const response = await reviewAppealFunc({ userId, appealId, decision, rejectionReason })
        const updatedAppeal = response.data as AppealDetailResponse
        this.updateAppealState(updatedAppeal)
        return updatedAppeal
      } catch (error) {
        throw new Error(this.handleError(error, 'reviewAppeal'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Fetches pending reports (admin only).
     */
    async getReports(request: {
      contentType?: ReportableEntity
      status?: string
      limit?: number
      startAfter?: string
    }): Promise<{
      success: boolean
      reports: ReportDetailResponse[]
      nextStartAfter: string | null
    }> {
      this.loading.reports = true
      try {
        const authStore = useAuthStore()
        if (!authStore.isAdmin) throw new Error('Only admins can view pending reports')
        const response = await getReportsFunc({
          limit: request.limit || 10,
          status: 'pending',
          ...request,
        })
        const { success, reports, nextStartAfter } = response.data as {
          success: boolean
          reports: ReportDetailResponse[]
          nextStartAfter: string | null
        }
        if (success) {
          const existingIds = new Set(this.pendingReports.map((r: ReportDetailResponse) => r.id))
          const newReports = reports.filter((r: ReportDetailResponse) => !existingIds.has(r.id))
          this.pendingReports = request.startAfter
            ? [...this.pendingReports, ...newReports]
            : newReports
          this.nextPostPageToken = nextStartAfter?.toString() || null
        }
        return { success, reports, nextStartAfter }
      } catch (error) {
        throw new Error(this.handleError(error, 'getReports'))
      } finally {
        this.loading.reports = false
      }
    },

    /**
     * Fetches all appeals for the authenticated user.
     */
    async getUserAppeals(
      request: { limit?: number; startAfter?: string } = {},
    ): Promise<AppealDetailResponse[]> {
      this.loading.appeals = true
      try {
        const limit = Math.min(request.limit || 10, 100)
        const response = await getUserAppealsFunc({
          limit,
          startAfter: request.startAfter,
        })
        const { success, appeals, nextStartAfter } = response.data as {
          success: boolean
          appeals: AppealDetailResponse[]
          nextStartAfter: string | null
        }
        if (success) {
          const existingIds = new Set(this.appealsList.map((a) => a.id))
          const newAppeals = appeals.filter((a) => !existingIds.has(a.id))
          newAppeals.forEach((appeal) => this.updateAppealState(appeal))
          this.myAppeals.appealIds = [
            ...this.myAppeals.appealIds.filter((id) => !new Set(appeals.map((a) => a.id)).has(id)),
            ...newAppeals.map((a) => a.id),
          ]
          this.myAppeals.nextStartAfter = nextStartAfter
        }
        return appeals
      } catch (error) {
        throw new Error(this.handleError(error, 'getUserAppeals'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Fetches all pending appeals (admin only).
     */
    async getPendingAppeals(request: { limit?: number; startAfter?: string } = {}): Promise<{
      success: boolean
      appeals: AppealDetailResponse[]
      nextStartAfter: string | null
    }> {
      this.loading.appeals = true
      try {
        const authStore = useAuthStore()
        if (!authStore.isAdmin) {
          throw new Error('Only admins can retrieve pending appeals')
        }
        const limit = Math.min(request.limit || 10, 100)
        const response = await getPendingAppealsFunc({
          limit,
          startAfter: request.startAfter,
        })
        const { success, appeals, nextStartAfter } = response.data as {
          success: boolean
          appeals: AppealDetailResponse[]
          nextStartAfter: string | null
        }
        if (success) {
          const existingIds = new Set(this.pendingAppeals.map((a) => a.id))
          const newAppeals = appeals.filter((a) => !existingIds.has(a.id))
          newAppeals.forEach((appeal) => this.updateAppealState(appeal))
          this.pendingAppeals = request.startAfter
            ? [...this.pendingAppeals, ...newAppeals]
            : newAppeals
          this.nextStartAfter = nextStartAfter
        }
        return { success, appeals, nextStartAfter }
      } catch (error) {
        throw new Error(this.handleError(error, 'getPendingAppeals'))
      } finally {
        this.loading.appeals = false
      }
    },

    /**
     * Sets up listeners for visible comments.
     */
    setupVisibleCommentListeners(postId: string, visibleCommentIds: string[]) {
      if (!navigator.onLine) {
        console.warn('Offline: Skipping setup of comment listeners')
        return
      }
      const activeListeners = new Set(
        commentCache.keys().filter((id) => commentCache.getEntry(id)?.unsubscribe),
      )
      visibleCommentIds.forEach((commentId) => {
        const comment = commentCache.get(commentId)
        if (comment && !activeListeners.has(commentId)) {
          this.setupCommentListener(postId, commentId, comment)
        }
      })
      commentCache.keys().forEach((commentId) => {
        const comment = commentCache.get(commentId)
        if (
          comment?.postId === postId &&
          !visibleCommentIds.includes(commentId) &&
          activeListeners.has(commentId)
        ) {
          this.cleanupCommentListener(commentId)
        }
      })
      this.logActiveListeners()
    },

    /**
     * Sets up listeners for visible replies.
     */
    setupVisibleReplyListeners(postId: string, commentId: string, visibleReplyIds: string[]) {
      if (!navigator.onLine) {
        console.warn('Offline: Skipping setup of reply listeners')
        return
      }
      const activeListeners = new Set(
        replyCache.keys().filter((id) => replyCache.getEntry(id)?.unsubscribe),
      )
      visibleReplyIds.forEach((replyId) => {
        const reply = replyCache.get(replyId)
        if (reply && !activeListeners.has(replyId)) {
          this.setupReplyListener(postId, commentId, replyId, reply)
        }
      })
      replyCache.keys().forEach((replyId) => {
        const reply = replyCache.get(replyId)
        if (
          reply?.commentId === commentId &&
          !visibleReplyIds.includes(replyId) &&
          activeListeners.has(replyId)
        ) {
          this.cleanupReplyListener(replyId)
        }
      })
      this.logActiveListeners()
    },

    /**
     * Refreshes all cache entries using CacheManager's refreshAllEntries.
     */
    async refreshAllCaches() {
      if (!navigator.onLine) {
        console.warn('Offline: Skipping cache refresh')
        return
      }
      try {
        await Promise.all([
          postCache.refreshAllEntries(),
          commentCache.refreshAllEntries(),
          replyCache.refreshAllEntries(),
        ])
        console.log('All caches refreshed successfully')
      } catch (error) {
        console.error('Error refreshing caches:', error)
      }
    },

    /**
     * Resets the store state and clears caches.
     */
    resetStore() {
      this.cleanupAllListeners()
      this.$reset()
      console.log('Store reset')
    },
  },
})

export function initializeCommunityStore() {
  const communityStore = useCommunityStore()
  communityStore.initializeCacheInvalidation()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    const communityStore = useCommunityStore()
    communityStore.cleanupAllListeners()
  })
}
